import { useState } from 'react'
import { Link, useParams } from 'react-router'
import { useTranslation } from 'react-i18next'

import { Lightbox } from '@/components/Lightbox'
import { SmartImage } from '@/components/SmartImage'
import { ErrorState } from '@/components/states'
import { NotFoundPage } from './NotFoundPage'
import { artworkRef, paths } from '@/lib/paths'
import { useArtwork } from '@/lib/queries'
import { artworkSchema, breadcrumbSchema, canonicalURL, useSeo } from '@/lib/seo'
import { useLocale } from '@/lib/useLocale'
import { ApiError } from '@/lib/api'

export function ArtworkPage() {
  const locale = useLocale()
  const { t } = useTranslation()
  const { ref = '' } = useParams()
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null)

  const { data: artwork, isPending, isError, error, refetch } = useArtwork(locale, ref)

  const photos = artwork?.photos ?? []
  const heroImage = photos[0]?.variants.find((v) => v.label === 'full')?.url

  // The canonical address is always id-plus-current-slug, whatever tail the
  // visitor arrived with. Without this, /work/42-old-name would be indexed as a
  // page of its own, competing with /work/42-new-name for the same content.
  const canonicalPath = artwork ? `/work/${artworkRef(artwork)}` : `/work/${ref}`

  useSeo({
    title: artwork ? `${artwork.title} — ${t('site.name')}` : t('site.name'),
    description:
      artwork?.description ||
      (artwork
        ? `${artwork.title} — ${artwork.categoryTitle}, ${t('site.name')}`
        : t('site.defaultDescription')),
    path: canonicalPath,
    locale,
    type: 'article',
    image: heroImage,
    imageAlt: artwork?.title,
    structuredData: artwork
      ? [
          artworkSchema({
            name: artwork.title,
            description: artwork.description || artwork.title,
            image: heroImage,
            dateCreated: artwork.createdOn,
            url: canonicalURL(locale, canonicalPath),
            artistName: t('site.name'),
            category: artwork.categoryTitle,
          }),
          breadcrumbSchema([
            { name: t('nav.home'), url: canonicalURL(locale, '/') },
            { name: t('gallery.title'), url: canonicalURL(locale, '/gallery') },
            {
              name: artwork.categoryTitle,
              url: canonicalURL(locale, `/gallery/${artwork.categorySlug}`),
            },
            { name: artwork.title, url: canonicalURL(locale, canonicalPath) },
          ]),
        ]
      : [],
  })

  // A missing artwork is a 404, not an error banner.
  if (isError && error instanceof ApiError && error.status === 404) {
    return <NotFoundPage />
  }

  if (isPending) {
    return (
      <div className="container-page py-14">
        <div className="animate-pulse">
          <div className="aspect-4/3 rounded-[var(--radius-card)] bg-stone-200/70" />
          <div className="mt-6 h-8 w-1/2 rounded bg-stone-200/70" />
        </div>
      </div>
    )
  }

  if (isError || !artwork) {
    return (
      <div className="container-page">
        <ErrorState onRetry={() => void refetch()} />
      </div>
    )
  }

  return (
    <div className="container-page py-10">
      <Link
        to={paths.category(locale, artwork.categorySlug)}
        className="link-underline text-sm text-stone-500"
      >
        ← {artwork.categoryTitle}
      </Link>

      <div className="mt-6 grid gap-10 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        <div>
          {photos.length > 0 ? (
            <>
              <button
                type="button"
                onClick={() => setLightboxIndex(0)}
                className="block w-full cursor-zoom-in overflow-hidden rounded-[var(--radius-card)] bg-stone-100"
              >
                <span className="sr-only">{t('artwork.openPhoto')}</span>
                <SmartImage
                  media={photos[0]}
                  locale={locale}
                  alt={artwork.title}
                  priority
                  preferred="full"
                  sizes="(min-width: 1024px) 60vw, 100vw"
                  className="w-full"
                />
              </button>

              {photos.length > 1 && (
                <div className="mt-3 grid grid-cols-4 gap-3 sm:grid-cols-6">
                  {photos.slice(1).map((photo, index) => (
                    <button
                      key={photo.id}
                      type="button"
                      onClick={() => setLightboxIndex(index + 1)}
                      className="aspect-square cursor-zoom-in overflow-hidden rounded-[var(--radius-card)] bg-stone-100"
                    >
                      <span className="sr-only">{t('artwork.openPhoto')}</span>
                      <SmartImage
                        media={photo}
                        locale={locale}
                        alt={artwork.title}
                        preferred="thumb"
                        sizes="120px"
                        className="h-full w-full"
                      />
                    </button>
                  ))}
                </div>
              )}
            </>
          ) : (
            <div className="aspect-4/3 rounded-[var(--radius-card)] bg-stone-100" />
          )}
        </div>

        <div className="lg:pt-2">
          <h1 className="text-4xl leading-tight">{artwork.title}</h1>

          <dl className="mt-6 space-y-2 text-sm">
            <div className="flex gap-2">
              <dt className="text-stone-400">{t('artwork.inSection')}:</dt>
              <dd>
                <Link
                  to={paths.category(locale, artwork.categorySlug)}
                  className="link-underline text-stone-700"
                >
                  {artwork.categoryTitle}
                </Link>
              </dd>
            </div>
            {artwork.createdOn && (
              <div className="flex gap-2">
                <dt className="text-stone-400">{t('artwork.createdOn')}:</dt>
                <dd>
                  <time dateTime={artwork.createdOn} className="text-stone-700 tabular-nums">
                    {formatDate(artwork.createdOn, locale)}
                  </time>
                </dd>
              </div>
            )}
          </dl>

          {artwork.description && (
            <div className="mt-6 space-y-4 leading-relaxed text-stone-600">
              {artwork.description.split(/\n{2,}/).map((paragraph, index) => (
                <p key={index}>{paragraph}</p>
              ))}
            </div>
          )}

          <aside className="mt-10 rounded-[var(--radius-card)] border border-stone-200 bg-white p-6">
            <h2 className="text-xl">{t('artwork.interested')}</h2>
            <p className="mt-2 text-sm leading-relaxed text-stone-600">
              {t('artwork.interestedText')}
            </p>
            <Link to={paths.contacts(locale)} className="btn btn-primary mt-5">
              {t('artwork.writeToUs')}
            </Link>
          </aside>
        </div>
      </div>

      {lightboxIndex !== null && (
        <Lightbox
          photos={photos}
          index={lightboxIndex}
          locale={locale}
          title={artwork.title}
          onClose={() => setLightboxIndex(null)}
          onNavigate={setLightboxIndex}
        />
      )}
    </div>
  )
}

function formatDate(iso: string, locale: string): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return iso
  return new Intl.DateTimeFormat(locale === 'uk' ? 'uk-UA' : 'en-GB', {
    year: 'numeric',
    month: 'long',
  }).format(date)
}
